import {
    CustomerFields,
    PaymentConfig,
    PaymentMethod,
    PaymentProvider
} from '../types';
import { PaymentError } from './payment-error';
import { PaymentProcessor } from './payment-processor';
import type { ProviderInitData } from './provider-types';

export type {
    AdyenProviderInitData,
    GoCardlessProviderInitData,
    StripeProviderInitData,
    WorldpayProviderInitData,
    ProviderInitData
} from './provider-types';
export {
    isAdyenInitData,
    isGoCardlessInitData,
    isStripeInitData,
    isWorldpayInitData
} from './provider-types';

export interface PaymentProviderConfig {
    provider: PaymentProvider;
    prepareForInitialization?: (
        paymentProcessor: PaymentProcessor,
        methodType: PaymentMethod,
        baseProviderData?: ProviderInitData
    ) => Promise<ProviderInitData>;
    initialize: (
        config: PaymentConfig,
        paymentMethod: PaymentMethod,
        providerInitData?: ProviderInitData,
        customerData?: CustomerFields
    ) => Promise<void>;
    destroy: () => void;
    setErrorHandler?: (errorHandler: (error: unknown) => void) => void;
    setPspErrorHandler?: (handler: (error: unknown) => void) => void;
    setClearErrorsHandler?: (handler: () => void) => void;
    setCancellationHandler?: (handler: (error?: PaymentError) => void) => void;
    setProcessingState?: (handler: (isProcessing: boolean) => void) => void;
    setValidationChangeHandler?: (handler: (isValid: boolean) => void) => void;
    mount?: (
        domNode: string | HTMLElement,
        paymentMethod: PaymentMethod
    ) => void;
    unmount?: () => void;
    submit?: () => void | Promise<void>;
    updateCustomerFields?: (customerData: CustomerFields) => void;
    setBacsConsent?: (consent: boolean) => void;
    setSignature?: (dataUrl: string | undefined) => void;
    setRequireSignature?: (required: boolean) => void;
}

type ProviderFactory = (
    paymentProcessor: PaymentProcessor
) => Promise<PaymentProviderConfig>;

const providerFactories: Record<PaymentProvider, ProviderFactory | null> = {
    ADYEN: async (
        paymentProcessor: PaymentProcessor
    ): Promise<PaymentProviderConfig> => {
        const { createAdyenProvider } =
            await import('../providers/adyen/register');
        return createAdyenProvider(paymentProcessor);
    },
    STRIPE: async (
        paymentProcessor: PaymentProcessor
    ): Promise<PaymentProviderConfig> => {
        const { createStripeProvider } =
            await import('../providers/stripe/register');
        return createStripeProvider(paymentProcessor);
    },
    WORLDPAY: async (
        paymentProcessor: PaymentProcessor
    ): Promise<PaymentProviderConfig> => {
        const { createWorldpayProvider } =
            await import('../providers/worldpay/register');
        return createWorldpayProvider(paymentProcessor);
    },
    GOCARDLESS: async (
        paymentProcessor: PaymentProcessor
    ): Promise<PaymentProviderConfig> => {
        const { createGoCardlessProvider } =
            await import('../providers/gocardless/register');
        return createGoCardlessProvider(paymentProcessor);
    },
    NONE: null,
    PAYRIXPRO: null
};

export const getProviderFactory = (
    provider?: PaymentProvider
): ProviderFactory => {
    const factory = provider ? providerFactories[provider] : null;

    if (!factory) {
        throw new PaymentError(
            `Provider ${provider} not configured`,
            'VALIDATION_ERROR'
        );
    }
    return factory;
};
